import { SerializedComponentsObject, SerializedComponent } from "../components/SerializedApp";
import { ListItemFlags } from "./array-screens/list-item-flags";
import { GlobalVariableNames } from "./description";
import { ComponentBuilder } from "./components";
import { RowIndexOrRowID, LetBinding } from "./serialized-component";
import { rowIndexColumnName } from "../google-sheets";

export function makeReusableComponents(builder: ComponentBuilder): SerializedComponentsObject {
    const { b } = builder;

    const components: SerializedComponentsObject = {};

    components.singletonItem = b.function(
        "_",
        ["obj"],
        b.if(
            b.builtinCall("IsArray", [b.getVariable("obj")]),
            b.withDefault(b.listNth(b.getVariable("obj"), b.constant(0)), b.data({})),
            b.getVariable("obj")
        )
    );
    components.naiveNumberOfItems = b.function(
        "_",
        ["list"],
        b.formatLocalizedString("numberOfItems", [b.listLength(b.getVariable("list"))])
    );
    components.smartNumberOfItems = b.function(
        "_",
        ["list", "none"],
        b.let(
            [["numItems", b.listLength(b.getVariable("list"))]],

            b.switch(
                b.getVariable("numItems"),
                [
                    [b.constant(0), b.getVariable("none")],
                    [b.constant(1), b.localizedString("oneItem")],
                ],
                b.if(
                    b.isLessThan(b.getVariable("numItems"), b.constant(5)),
                    b.formatLocalizedString("twoToFourItems", [b.getVariable("numItems")]),
                    b.naiveNumberOfItems(b.getVariable("list"))
                )
            )
        )
    );
    components.smartListItem = b.function(
        "list",
        ["caption", "onTap"],
        b.switch(
            b.listLength(b.getVariable("list")),
            [[b.constant(0), b.emptyUI]],
            b.adaptiveListItem(
                b.smartNumberOfItems(b.getVariable("list"), b.constant(null)),
                b.getVariable("caption"),
                undefined,
                undefined,
                undefined,
                undefined,
                b.getVariable("onTap"),
                undefined,
                undefined,
                b.constant(ListItemFlags.WrapText | ListItemFlags.InvertTitleAndSubtitle),
                undefined,
                undefined,
                undefined
            )
        )
    );
    components.pageTitleSmartLink = b.function(
        "link",
        ["caption", "onTap"],
        b.let(
            [["data", b.classifyLink(b.getVariable("link"))]],
            b.adaptiveListItem(
                b.getNonTableProperty("title", b.getVariable("data")),
                b.withDefault(b.getVariable("caption"), b.getNonTableProperty("caption", b.getVariable("data"))),
                undefined,
                undefined,
                b.getNonTableProperty("icon", b.getVariable("data")),
                undefined,
                b.getVariable("onTap"),
                undefined,
                undefined,
                b.constant(ListItemFlags.WrapText | ListItemFlags.InvertTitleAndSubtitle),
                undefined,
                undefined,
                undefined
            )
        )
    );
    components.urlSmartLink = b.function(
        "link",
        ["displayLink", "caption", "onTap"],
        b.let(
            [["data", b.classifyLink(b.getVariable("link"))]],
            b.adaptiveListItem(
                b.getVariable("displayLink"),
                b.withDefault(b.getVariable("caption"), b.getNonTableProperty("caption", b.getVariable("data"))),
                undefined,
                undefined,
                b.getNonTableProperty("icon", b.getVariable("data")),
                undefined,
                b.getVariable("onTap"),
                undefined,
                undefined,
                b.constant(ListItemFlags.WrapText | ListItemFlags.InvertTitleAndSubtitle),
                undefined,
                undefined,
                undefined
            )
        )
    );
    components.shortURLSmartLink = b.function(
        "link",
        ["caption", "onTap"],
        b.let(
            [
                ["data", b.classifyLink(b.getVariable("link"))],
                ["title", b.shortenLink(b.getVariable("link"))],
            ],
            b.adaptiveListItem(
                b.getVariable("title"),
                b.withDefault(b.getVariable("caption"), b.getNonTableProperty("caption", b.getVariable("data"))),
                undefined,
                undefined,
                b.getNonTableProperty("icon", b.getVariable("data")),
                undefined,
                b.getVariable("onTap"),
                undefined,
                undefined,
                b.constant(ListItemFlags.WrapText | ListItemFlags.InvertTitleAndSubtitle),
                undefined,
                undefined,
                undefined
            )
        )
    );
    components.lastPathComponentSmartLink = b.function(
        "link",
        ["caption", "onTap"],
        b.let(
            [
                ["data", b.classifyLink(b.getVariable("link"))],
                [
                    "title",
                    b.withDefault(
                        b.getLastURLPathComponent(b.getVariable("link")),
                        b.shortenLink(b.getVariable("link"))
                    ),
                ],
            ],
            b.adaptiveListItem(
                b.getVariable("title"),
                b.withDefault(b.getVariable("caption"), b.getNonTableProperty("caption", b.getVariable("data"))),
                undefined,
                undefined,
                b.getNonTableProperty("icon", b.getVariable("data")),
                undefined,
                b.getVariable("onTap"),
                undefined,
                undefined,
                b.constant(ListItemFlags.WrapText | ListItemFlags.InvertTitleAndSubtitle),
                undefined,
                undefined,
                undefined
            )
        )
    );
    components.captionAsTitleSmartLink = b.function(
        "link",
        ["caption", "onTap"],
        b.let(
            [["data", b.classifyLink(b.getVariable("link"))]],
            b.adaptiveListItem(
                b.withDefault(b.getVariable("caption"), b.getNonTableProperty("caption", b.getVariable("data"))),
                undefined,
                undefined,
                undefined,
                b.getNonTableProperty("icon", b.getVariable("data")),
                undefined,
                b.getVariable("onTap"),
                undefined,
                undefined,
                b.constant(ListItemFlags.WrapText | ListItemFlags.InvertTitleAndSubtitle),
                undefined,
                undefined,
                undefined
            )
        )
    );
    components.phoneCall = b.function(
        "_",
        ["number"],
        b.openLink(b.builtinCall("MakeTelURL", [b.getVariable("number")]))
    );
    components.textMessage = b.function(
        "_",
        ["number", "body"],
        b.openLink(b.builtinCall("MakeSMSURL", [b.getVariable("number"), b.getVariable("body")]))
    );

    if (builder.isBuilder || builder.makeUserProfileRow() !== undefined) {
        components.fetchUserProfileRows = b.fetchTableRows(
            "default",
            b.lookupGlobalVariable(GlobalVariableNames.UserProfileTableName),
            false
        );
        components.userProfilePropertiesFromRow = b.function(
            "row",
            [],
            b.object({
                email: b.getDynamicProperty(
                    b.lookupGlobalVariable(GlobalVariableNames.UserProfileEmailColumnName),
                    b.getVariable("row")
                ),
                name: b.getDynamicProperty(
                    b.lookupGlobalVariable(GlobalVariableNames.UserProfileNameColumnName),
                    b.getVariable("row")
                ),
                image: b.getDynamicProperty(
                    b.lookupGlobalVariable(GlobalVariableNames.UserProfileImageColumnName),
                    b.getVariable("row")
                ),
            })
        );
        components.userProfileRow = b.global(
            "userProfileRow",
            b.let(
                [
                    ["email", b.verifiedEmailAddress()],
                    [
                        "fetchedRow",
                        b.find(
                            b.lookupFunction("fetchUserProfileRows"),
                            b.areEmailsEqual(
                                b.getVariable("email"),
                                b.getDynamicProperty(
                                    b.lookupGlobalVariable(GlobalVariableNames.UserProfileEmailColumnName),
                                    b.getContext
                                )
                            )
                        ),
                    ],
                    ["profileRow", b.builtinCall("LoginUserProfileRow", [])],
                ],
                b.withDefault(
                    b.getVariable("fetchedRow"),
                    b.withDefault(
                        b.getVariable("profileRow"),
                        b.addComputedColumnsToData(
                            b.lookupGlobalVariable(GlobalVariableNames.UserProfileTableName),
                            b.objectFromKeyAndValue(
                                b.lookupGlobalVariable(GlobalVariableNames.UserProfileEmailColumnName),
                                b.getVariable("email")
                            )
                        )
                    )
                )
            )
        );
        components.userProfileProperties = b.bindContextToValue(
            b.functionCall("userProfilePropertiesFromRow", []),
            b.lookupFunction("userProfileRow")
        );
        components.allUserProfileProperties = b.map(
            b.lookupFunction("fetchUserProfileRows"),
            b.functionCall("userProfilePropertiesFromRow", [])
        );

        const userProfile = b.lookupFunction("userProfileRow");
        const lets: LetBinding[] = [["userProfile", userProfile]];

        const nameColumnName = b.lookupGlobalVariable(GlobalVariableNames.UserProfileNameColumnName);
        const nameProperty = b.getDynamicProperty(nameColumnName, b.getVariable("userProfile"));
        const emailColumnName = b.lookupGlobalVariable(GlobalVariableNames.UserProfileEmailColumnName);
        const rowIndexOrRowID: RowIndexOrRowID<SerializedComponent> = {
            keyColumnName: emailColumnName,
            keyColumnValue: b.getDynamicProperty(emailColumnName, b.getVariable("userProfile")),
            rowIndex: b.getNonTableProperty(rowIndexColumnName, b.getVariable("userProfile")),
        };

        const setColumnsData = b.setColumnInRowForProperty(
            nameColumnName,
            nameProperty,
            b.lookupGlobalVariable(GlobalVariableNames.UserProfileTableName),
            rowIndexOrRowID,
            false
        );

        components.userProfileName = b.let(lets, nameProperty);

        if (setColumnsData.action !== undefined && setColumnsData.isEnabled !== undefined) {
            components.userProfileSetName = b.let(lets, setColumnsData.action);
            components.userProfileCanSetName = b.let(lets, setColumnsData.isEnabled);
        }
    }

    return components;
}
